import {
  TAB_TO_PATH,
  STORY_IDS,
  NAV_DIR,
  getTabFromPath,
  getStoryFromPath,
  getNextTabId,
  easeOutQuart,
} from "./utils.js";

(function () {
  try {
    let fadeObserver = null;
    let countObserver = null;

    const SCROLL_THRESHOLD = 40;
    const STAGGER_MS = 80;
    const COUNT_DURATION_MS = 1500;
    const FADE_OBSERVER_THRESHOLD = 0.1;
    const FADE_ROOT_MARGIN = "0px 0px -40px 0px";
    const COUNT_OBSERVER_THRESHOLD = 0.3;

    const header = document.getElementById("site-header");
    const navToggle = document.getElementById("nav-toggle");
    const navLinks = document.getElementById("nav-links");
    const allNavLinks = document.querySelectorAll(".nav-link");
    const allTabLinks = document.querySelectorAll("[data-tab]");
    const panels = document.querySelectorAll(".tab-panel");

    function deactivateAllPanels() {
      panels.forEach(function (panel) {
        panel.querySelectorAll(".fade-up.visible").forEach(function (el) {
          el.classList.remove("visible");
        });
        panel.querySelectorAll("[data-count].counted").forEach(function (el) {
          el.classList.remove("counted");
        });
        panel.classList.remove("active");
      });
    }

    function updateNavState(activeTabId) {
      allNavLinks.forEach(function (link) {
        const isActive = link.getAttribute("data-tab") === activeTabId;
        link.classList.toggle("active", isActive);
        link.setAttribute("aria-selected", isActive ? "true" : "false");
        link.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    }

    function activatePanel(tabId) {
      const target = document.getElementById(tabId);
      if (!target) {
        console.warn("Belonging Campus: no panel found for tab", tabId);
        return;
      }
      target.classList.add("active");
    }

    function switchTab(tabId, path) {
      deactivateAllPanels();
      updateNavState(tabId);
      activatePanel(tabId);
      window.dispatchEvent(
        new window.CustomEvent("belonging:tabchange", {
          detail: {
            tabId: tabId,
            path: path || window.location.pathname,
          },
        })
      );
      window.scrollTo({ top: 0, behavior: "instant" });
      closeNav();
      observeFadeUps();
      observeCountUps();
    }

    function navigateToTab(tab, path) {
      switchTab(tab, path);
      history.pushState({ tab: tab }, "", path);
    }

    function openNav() {
      if (!navLinks || !navToggle) {
        return;
      }
      navLinks.classList.add("open");
      navToggle.setAttribute("aria-expanded", "true");
      document.body.classList.add("nav-open");
      const firstLink = navLinks.querySelector(".nav-link");
      if (firstLink) {
        firstLink.focus();
      }
    }

    function closeNav() {
      if (!navLinks || !navToggle) {
        return;
      }
      const wasOpen = navLinks.classList.contains("open");
      navLinks.classList.remove("open");
      navToggle.setAttribute("aria-expanded", "false");
      document.body.classList.remove("nav-open");
      if (wasOpen) {
        navToggle.focus();
      }
    }

    allTabLinks.forEach(function (link) {
      link.addEventListener("click", function (e) {
        e.preventDefault();
        const tab = this.getAttribute("data-tab");
        if (tab) {
          const href = this.getAttribute("href") || "";
          const path = href.startsWith("/") ? href : TAB_TO_PATH[tab] || "/";
          navigateToTab(tab, path);
          const hash = (path.match(/#(.+)$/) || [])[1];
          if (hash) {
            const el = document.getElementById(hash);
            if (el) {
              requestAnimationFrame(function () {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }
          }
        }
      });
    });

    if (navLinks) {
      navLinks.addEventListener("keydown", function (e) {
        const current = document.activeElement;
        const currentTab = current ? current.getAttribute("data-tab") : null;
        if (!currentTab) {
          return;
        }

        let direction;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          direction = NAV_DIR.NEXT;
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          direction = NAV_DIR.PREV;
        } else if (e.key === "Home") {
          e.preventDefault();
          direction = NAV_DIR.HOME;
        } else if (e.key === "End") {
          e.preventDefault();
          direction = NAV_DIR.END;
        } else if (e.key === "Escape") {
          closeNav();
          return;
        } else {
          return;
        }

        const nextTabId = getNextTabId(currentTab, direction);
        const nextLink = navLinks.querySelector('[data-tab="' + CSS.escape(nextTabId) + '"]');
        if (nextLink) {
          nextLink.focus();
          navigateToTab(nextTabId, TAB_TO_PATH[nextTabId] || "/");
        }
      });
    }

    function scrollToHash() {
      const hash = window.location.hash;
      if (hash) {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }
    }

    const initialTab = getTabFromPath(window.location.pathname);
    switchTab(initialTab);
    scrollToHash();

    window.addEventListener("popstate", function () {
      const tab = getTabFromPath(window.location.pathname);
      switchTab(tab, window.location.pathname);
      scrollToHash();
    });

    if (navToggle && navLinks) {
      navToggle.addEventListener("click", function () {
        const isOpen = navLinks.classList.contains("open");
        if (isOpen) {
          closeNav();
        } else {
          openNav();
        }
      });
    }

    document.addEventListener("click", function (e) {
      if (navLinks && navToggle && !navLinks.contains(e.target) && !navToggle.contains(e.target)) {
        closeNav();
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && navLinks && navLinks.classList.contains("open")) {
        closeNav();
      }
    });

    window.addEventListener(
      "scroll",
      function () {
        if (!header) {
          return;
        }
        if (window.scrollY > SCROLL_THRESHOLD) {
          header.classList.add("scrolled");
        } else {
          header.classList.remove("scrolled");
        }
      },
      { passive: true }
    );

    function createSectionObserver(selector, options, onIntersect, skipClass, fallback) {
      const elements = document.querySelectorAll(selector);

      if (!("IntersectionObserver" in window)) {
        if (fallback) {
          fallback(elements);
        }
        return null;
      }

      const observer = new IntersectionObserver(onIntersect, options);

      elements.forEach(function (el) {
        if (!el.classList.contains(skipClass)) {
          observer.observe(el);
        }
      });

      return observer;
    }

    function observeFadeUps() {
      if (fadeObserver) {
        fadeObserver.disconnect();
      }

      fadeObserver = createSectionObserver(
        ".tab-panel.active .fade-up",
        { threshold: FADE_OBSERVER_THRESHOLD, rootMargin: FADE_ROOT_MARGIN },
        function (entries) {
          const visible = entries.filter(function (entry) {
            return entry.isIntersecting;
          });
          visible.forEach(function (entry, i) {
            setTimeout(function () {
              entry.target.classList.add("visible");
            }, i * STAGGER_MS);
            fadeObserver.unobserve(entry.target);
          });
        },
        "visible",
        function (elements) {
          elements.forEach(function (el) {
            el.classList.add("visible");
          });
        }
      );
    }

    function animateCount(el) {
      const target = parseInt(el.getAttribute("data-count"), 10);
      const suffix = el.getAttribute("data-suffix") || "";
      let start = null;

      if (isNaN(target)) {
        el.textContent = (el.getAttribute("data-count") || "0") + suffix;
        return;
      }
      if (target === 0) {
        el.textContent = "0" + suffix;
        return;
      }

      function step(timestamp) {
        if (!start) start = timestamp;
        const progress = Math.min((timestamp - start) / COUNT_DURATION_MS, 1);
        const current = Math.round(easeOutQuart(progress) * target);
        el.textContent = current + suffix;
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      }

      el.textContent = "0" + suffix;
      requestAnimationFrame(step);
    }

    function observeCountUps() {
      if (countObserver) {
        countObserver.disconnect();
      }

      countObserver = createSectionObserver(
        ".tab-panel.active [data-count]",
        { threshold: COUNT_OBSERVER_THRESHOLD },
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!entry.target.classList.contains("counted")) {
                entry.target.classList.add("counted");
                animateCount(entry.target);
              }
              countObserver.unobserve(entry.target);
            }
          });
        },
        "counted",
        null
      );
    }

    function initStoriesHub() {
      const hub = document.getElementById("stories-hub");
      const reader = document.getElementById("story-reader");
      const slides = document.getElementById("story-slides");
      const backBtn = document.getElementById("story-reader-back");
      const prevBtn = document.getElementById("story-reader-prev");
      const nextBtn = document.getElementById("story-reader-next");
      const counterEl = document.getElementById("story-reader-counter");
      const storiesPanel = document.getElementById("stories");

      if (!hub || !reader || !slides) return;

      let activeIndex = 0;
      const continueEl = document.getElementById("story-reader-continue");
      let continueObserver = null;

      function enterReadingMode() {
        if (storiesPanel) storiesPanel.classList.add("stories--reading");
        if (continueEl) continueEl.classList.remove("visible");
        observeContinue();
      }

      function exitReadingMode() {
        if (storiesPanel) storiesPanel.classList.remove("stories--reading");
        if (continueObserver) continueObserver.disconnect();
      }

      function observeContinue() {
        if (continueObserver) continueObserver.disconnect();
        if (!continueEl || !("IntersectionObserver" in window)) {
          if (continueEl) continueEl.classList.add("visible");
          return;
        }
        continueObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                continueEl.classList.add("visible");
                continueObserver.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.5 }
        );
        continueObserver.observe(continueEl);
      }

      function showHub(options) {
        const shouldPushState = !options || options.pushState !== false;
        hub.removeAttribute("hidden");
        reader.setAttribute("hidden", "");
        exitReadingMode();
        if (shouldPushState) {
          history.pushState({ tab: "stories" }, "", "/stories");
        }
      }

      function showReader(index) {
        hub.setAttribute("hidden", "");
        reader.removeAttribute("hidden");
        enterReadingMode();
        activeIndex = Math.max(0, Math.min(index, STORY_IDS.length - 1));
        updateSlide();
        reader.scrollIntoView({ behavior: "smooth", block: "start" });
        const storyId = STORY_IDS[activeIndex];
        history.pushState({ tab: "stories", story: storyId }, "", "/stories/" + storyId);
      }

      function syncFromUrl() {
        if (!storiesPanel || !storiesPanel.classList.contains("active")) return;
        const storyId = getStoryFromPath(window.location.pathname);
        if (storyId) {
          const idx = STORY_IDS.indexOf(storyId);
          if (idx >= 0) {
            hub.setAttribute("hidden", "");
            reader.removeAttribute("hidden");
            enterReadingMode();
            activeIndex = idx;
            updateSlide();
            reader.scrollIntoView({ behavior: "instant", block: "start" });
          }
        } else {
          hub.removeAttribute("hidden");
          reader.setAttribute("hidden", "");
          exitReadingMode();
        }
      }

      function updateSlide() {
        const storyId = STORY_IDS[activeIndex];
        slides.querySelectorAll(".story-slide").forEach(function (slide) {
          slide.classList.toggle("active", slide.getAttribute("data-story") === storyId);
        });
        if (prevBtn) prevBtn.disabled = activeIndex <= 0;
        if (nextBtn) nextBtn.disabled = activeIndex >= STORY_IDS.length - 1;
        if (counterEl) {
          counterEl.textContent = activeIndex + 1 + " / " + STORY_IDS.length;
        }
      }

      hub.querySelectorAll(".story-picker").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          const storyId = btn.getAttribute("data-story");
          const idx = STORY_IDS.indexOf(storyId);
          if (idx >= 0) showReader(idx);
        });
      });

      if (backBtn) backBtn.addEventListener("click", showHub);
      if (prevBtn)
        prevBtn.addEventListener("click", function () {
          if (activeIndex > 0) showReader(activeIndex - 1);
        });
      if (nextBtn)
        nextBtn.addEventListener("click", function () {
          if (activeIndex < STORY_IDS.length - 1) showReader(activeIndex + 1);
        });

      window.addEventListener("popstate", syncFromUrl);
      window.addEventListener("belonging:tabchange", function (event) {
        const detail = event.detail || {};
        if (detail.tabId !== "stories") {
          showHub({ pushState: false });
          return;
        }

        if (!getStoryFromPath(detail.path || window.location.pathname)) {
          showHub({ pushState: false });
        }
      });
      syncFromUrl();
    }

    function initMailtoLinks() {
      const user = "shadi" + "." + "mehrabi";
      const domain = "gmail" + "." + "com";
      document.querySelectorAll("[data-mailto-subject]").forEach(function (el) {
        el.addEventListener("click", function (e) {
          e.preventDefault();
          const subject = el.getAttribute("data-mailto-subject") || "Belonging Campus";
          window.location.href =
            "mailto:" + user + "@" + domain + "?subject=" + encodeURIComponent(subject);
        });
      });
    }

    function initFilterChips() {
      var chips = document.querySelectorAll(".filter-chip[data-filter]");
      var countryBlocks = document.querySelectorAll(".country-block");
      if (!chips.length || !countryBlocks.length) return;

      chips.forEach(function (chip) {
        chip.addEventListener("click", function () {
          var filter = chip.getAttribute("data-filter");
          chips.forEach(function (c) {
            c.classList.toggle("active", c === chip);
          });
          countryBlocks.forEach(function (block) {
            if (filter === "all") {
              block.style.display = "";
            } else {
              var isMatch = block.classList.contains("country-block--" + filter);
              block.style.display = isMatch ? "" : "none";
            }
          });
        });
      });
    }

    function initAccordions() {
      var triggers = document.querySelectorAll(".accordion-trigger");
      triggers.forEach(function (trigger) {
        trigger.addEventListener("click", function () {
          var section = trigger.closest(".implications-section");
          var body = section ? section.querySelector(".accordion-body") : null;
          var isOpen = trigger.getAttribute("aria-expanded") === "true";
          trigger.setAttribute("aria-expanded", isOpen ? "false" : "true");
          if (body) {
            body.hidden = isOpen;
          }
        });
      });
    }

    [
      observeFadeUps,
      observeCountUps,
      initStoriesHub,
      initMailtoLinks,
      initFilterChips,
      initAccordions,
    ].forEach(function (fn) {
      try {
        fn();
      } catch (err) {
        console.error("Belonging Campus " + fn.name + " error:", err);
      }
    });
  } catch (err) {
    console.error("Belonging Campus init error:", err);
  }
})();
